import AssignmentForm from '@app/components/AssignmentForm'
import CheckBox from '@app/components/CheckBox'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import MentionEditor from '@app/components/MentionEditor'
import {
  FeedItemSourceType,
  FeedItemType,
  LeadPriority,
} from '@app/models/enums'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown,
} from 'reactstrap'
import * as FeedItemEditorStore from '../modules/feedItemEditor'
import './FeedItemEditor.scss'

export interface IFeedItemEditorUIProps {
  contextId?: string
  contextType?: FeedItemSourceType
  itemType?: FeedItemType
}

type FeedItemEditorProps = IFeedItemEditorUIProps &
  FeedItemEditorStore.IFeedItemEditorState &
  typeof FeedItemEditorStore.actionCreators

export default class FeedItemEditor extends React.PureComponent<
  FeedItemEditorProps,
  {}
> {
  constructor(props: any) {
    super(props)

    const feedItemType = this.props.itemType || FeedItemType.Post
    const tabIndex =
      feedItemType === FeedItemType.Post
        ? 1
        : feedItemType === FeedItemType.Assignment
        ? 3
        : 2

    this.props.setSelectedTab(tabIndex)
  }

  public toggle() {
    this.props.toggleAssignmentModal()
  }

  public renderAssignmentModal() {
    return (
      <Modal
        isOpen={this.props.isAssignmentModalOpen}
        toggle={() => this.toggle()}
        size='lg'
      >
        <ModalHeader toggle={() => this.toggle()}>New Assignment</ModalHeader>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <AssignmentForm />
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => this.toggle()}>
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => this.props.attemptCreateAssignmentForm()}
          >
            OK
          </Button>{' '}
        </ModalFooter>
      </Modal>
    )
  }

  public render() {
    const dropzoneStyle: React.CSSProperties = {
      borderWidth: '0px',
      width: '100%',
      padding: '0px',
    }

    const selectPhotoStyle: React.CSSProperties = {
      borderWidth: '0px',
      padding: '0px',
    }

    let priorityIconName: IconName
    let iconClasses: string | undefined
    priorityIconName = 'minus-circle'

    switch (this.props.leadPriority) {
      case LeadPriority.High:
        iconClasses = 'text-danger'
        priorityIconName = 'exclamation-circle'
        break

      case LeadPriority.Low:
        iconClasses = 'text-info'
        priorityIconName = 'arrow-alt-circle-down'
        break

      default:
        priorityIconName = 'minus-circle'
        break
    }

    return (
      <div>
        <div className='feed-item-editor d-flex flex-column'>
          <div className='d-flex flex-row feed-item-header'>
            <div className='mr-3 mr-md-4'>
              <a
                className={`profile-view-toggle ${
                  this.props.selectedTab === 1 ? 'active' : ''
                }`}
                onClick={() => this.props.setSelectedTab(1)}
              >
                <FontAwesomeIcon icon={['fal', 'file']} fixedWidth /> Post
              </a>
            </div>
            <div className='mr-3 mr-md-4'>
              <a
                className={`profile-view-toggle ${
                  this.props.selectedTab === 2 ? 'active' : ''
                }`}
                onClick={() => this.props.setSelectedTab(2)}
              >
                <FontAwesomeIcon icon={['fal', 'map-pin']} fixedWidth /> Lead
              </a>
            </div>
            <div className='mr-3 mr-md-4'>
              <a
                className={`profile-view-toggle ${
                  this.props.selectedTab === 3 ? 'active' : ''
                }`}
                onClick={() => {
                  this.props.setSelectedTab(3)
                  this.toggle()
                }}
              >
                <FontAwesomeIcon icon={['fal', 'check-square']} fixedWidth />{' '}
                Assignment
              </a>
              {this.renderAssignmentModal()}
            </div>
          </div>
          <div className='feed-item-body'>
            <div
              className='feed-item-title input-group'
              style={{
                display: this.props.selectedTab === 2 ? 'flex' : 'none',
              }}
            >
              <div className='input-group-prepend'>
                <UncontrolledDropdown>
                  <DropdownToggle
                    color='light'
                    style={{ borderRadius: 0 }}
                    className='align-items-center'
                  >
                    <FontAwesomeIcon
                      className={`mr-2 ${iconClasses}`}
                      icon={['fal', priorityIconName]}
                    />
                    <FontAwesomeIcon
                      style={{ fontSize: '0.8rem' }}
                      icon={['fas', 'caret-down']}
                      className='mr-2'
                    />
                  </DropdownToggle>
                  {this._renderDropdown()}
                </UncontrolledDropdown>
              </div>
              <input
                type='text'
                value={this.props.titleText}
                style={{ borderRight: '1px solid #ddd' }}
                onChange={(e) => this.props.setTitleText(e.target.value)}
                className='form-control input-no-border'
                placeholder='Title'
              />

              <div className='input-group-append'>
                <Input
                  type='select'
                  className='input-no-border'
                  value={this.props.leadType}
                  onChange={(e) => this.props.setLeadType(e.target.value)}
                >
                  <option value='BuyerUser'>Buyer / User</option>
                  <option value='BuyerInvestor'>Buyer / Investor</option>
                  <option value='DevelopmentClient'>Development Client</option>
                  <option value='Other'>Other</option>
                  <option value='Seller'>Seller</option>
                  <option value='Tenant'>Tenant</option>
                </Input>
              </div>
            </div>
            <div className='feed-item-text'>
              <FileDropzone
                style={dropzoneStyle}
                identifier='feed-item-editor'
                disableClick={true}
              >
                <MentionEditor
                  id='new-feed-item'
                  editorState={this.props.postText}
                  onChange={this.props.setPostText}
                  placeholder='Something to add?'
                />
              </FileDropzone>
              <FileProgress identifier='feed-item-editor' />
            </div>
          </div>
          <div className='d-flex flex-row feed-item-footer'>
            <div className='mr-auto' style={{ alignSelf: 'center' }}>
              <CheckBox
                className='text-white'
                onChange={(newValue) => this.props.togglePublic()}
                value={this.props.makePublic}
              >
                Make public
              </CheckBox>
            </div>
            <div style={{ alignItems: 'center' }}>
              <div className='feed-item-buttons'>
                <FileDropzone
                  identifier='feed-item-editor'
                  style={selectPhotoStyle}
                >
                  <button className='btn btn-primary mr-2'>
                    <FontAwesomeIcon
                      icon={['fal', 'camera']}
                      className='feed-item-camera'
                    />
                  </button>
                </FileDropzone>
              </div>
              <div className='feed-item-buttons'>
                <button
                  className='btn btn-primary'
                  disabled={this.props.attemptingCreate}
                  onClick={() =>
                    this.props.attemptCreateFeedItem(
                      this.props.contextId,
                      this.props.contextType,
                    )
                  }
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private _renderDropdown() {
    const items = []

    items.push(
      <DropdownItem
        onClick={() => this.props.setLeadPriority('High')}
        key='high'
      >
        <FontAwesomeIcon
          className='mr-2 text-danger'
          icon={['fal', 'exclamation-circle']}
        />
        High
      </DropdownItem>,
    )

    items.push(
      <DropdownItem
        onClick={() => this.props.setLeadPriority('Medium')}
        key='medium'
      >
        <FontAwesomeIcon className='mr-2' icon={['fal', 'minus-circle']} />
        Medium
      </DropdownItem>,
    )

    items.push(
      <DropdownItem onClick={() => this.props.setLeadPriority('Low')} key='low'>
        <FontAwesomeIcon
          className='mr-2 text-info'
          icon={['fal', 'arrow-alt-circle-down']}
        />
        Low
      </DropdownItem>,
    )

    return <DropdownMenu>{items}</DropdownMenu>
  }
}
